import React, { useState, useEffect, useMemo } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormHelperText,
  styled,
  Typography,
} from '@mui/material';

const FormField = styled('div')(({ theme }) => ({
  flex: '1 1 calc(50% - 16px)',
  minWidth: '240px'
}));

interface ServiceOption {
  id: number;
  serviceIndex: number;
  baseServiceId: number;
  name: string;
  carOptions: any[];
}

interface FleetOption {
  id: number;
  fleetIndex: number;
  actualDatabaseId: number;
  displayName: string;
  fleetDetails: any;
  slot?: string;
}

interface ServiceSelectionFormProps {
  serviceOptions: ServiceOption[];
  fleetOptions: FleetOption[];
  selectedServiceId: string;
  selectedFleetId: string;
  onServiceChange: (serviceId: string) => void;
  onFleetChange: (fleetId: string) => void;
  onAirportSelectionChange: (selection: string) => void;
  onOnewaySelectionChange: (selection: string) => void;
  onPerPaxSelectionChange: (selection: string) => void;
  errors: Record<string, string>;
  isMumbaiAirportService: boolean;
  isOnewayService: boolean;
  isPerPaxService: boolean;
  isOtherService: boolean;
  airportPickupDrop: string;
  onewayRoute: string;
  perPaxRoute: string;
}

const ServiceSelectionForm: React.FC<ServiceSelectionFormProps> = ({
  serviceOptions,
  fleetOptions,
  selectedServiceId,
  selectedFleetId,
  onServiceChange,
  onFleetChange,
  onAirportSelectionChange,
  onOnewaySelectionChange,
  onPerPaxSelectionChange,
  errors,
  isMumbaiAirportService,
  isOnewayService,
  isPerPaxService,
  isOtherService,
  airportPickupDrop,
  onewayRoute,
  perPaxRoute,
}) => {

   // Auto-select fleet when "Others" service is selected
  useEffect(() => {
    if (isOtherService && fleetOptions.length > 0 && !selectedFleetId) {
      // Automatically select the first fleet option for "Others" service
      onFleetChange(fleetOptions[0].id.toString());
    }
  }, [isOtherService, fleetOptions, selectedFleetId, onFleetChange]);
  
  const handleServiceChange = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    onServiceChange(value);
  };

  const handleFleetChange = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    onFleetChange(value);
  };

  const handleAirportPickupDropChange = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    onAirportSelectionChange(value);
  };

  const handleOnewayRouteChange = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    onOnewaySelectionChange(value);
  };

  const handlePerPaxRouteChange = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    onPerPaxSelectionChange(value);
  };
  return (
    <>
      <FormField>
        <FormControl fullWidth error={!!errors.service}>
          <Select
            labelId="service-label"
            value={selectedServiceId}
            // label="Select Service"
            onChange={handleServiceChange}
            displayEmpty
          >
            <MenuItem value="" disabled>Select Service Type</MenuItem>
            {serviceOptions.map((service) => (
              <MenuItem key={service.id} value={service.id.toString()}>
                {service.name}
              </MenuItem>
            ))}
          </Select>
          {errors.service && <FormHelperText>{errors.service}</FormHelperText>}
        </FormControl>
      </FormField>

      {/* Mumbai Airport Pickup/Drop Selection */}
      {isMumbaiAirportService && (
        <FormField>
          <FormControl fullWidth error={!!errors.airportPickupDrop}>
            {/* <InputLabel id="airport-pickup-drop-label">Pickup or Drop</InputLabel> */}
            <Select
              labelId="airport-pickup-drop-label"
              value={airportPickupDrop}
              // label="Pickup or Drop"
              onChange={handleAirportPickupDropChange}
              displayEmpty
            >
              <MenuItem value="" disabled>Pickup or Drop</MenuItem>
              <MenuItem value="pickup">
                Pickup from Mumbai Airport → Pune
              </MenuItem>
              <MenuItem value="drop">
                Drop to Mumbai Airport ← Pune
              </MenuItem>
            </Select>
            {errors.airportPickupDrop && <FormHelperText>{errors.airportPickupDrop}</FormHelperText>}
          </FormControl>
        </FormField>
      )}

      {/* Oneway Route Selection */}
      {isOnewayService && (
        <FormField>
          <FormControl fullWidth error={!!errors.onewayRoute}>
            {/* <InputLabel id="oneway-route-label">Select Route</InputLabel> */}
            <Select
              labelId="oneway-route-label"
              value={onewayRoute}
              // label="Select Route"
              onChange={handleOnewayRouteChange}
              displayEmpty
            >
              <MenuItem value="" disabled>Select Route</MenuItem>
              <MenuItem value="pune-to-mumbai">
                Pune → Mumbai
              </MenuItem>
              <MenuItem value="mumbai-to-pune">
                Mumbai → Pune
              </MenuItem>
            </Select>
            {errors.onewayRoute && <FormHelperText>{errors.onewayRoute}</FormHelperText>}
          </FormControl>
        </FormField>
      )}

      {/* Per Pax Route Selection */}
      {isPerPaxService && (
        <FormField>
          <FormControl fullWidth error={!!errors.perPaxRoute}>
            {/* <InputLabel id="perpax-route-label">Select Route</InputLabel> */}
            <Select
              labelId="perpax-route-label"
              value={perPaxRoute}
              // label="Select Route"
              onChange={handlePerPaxRouteChange}
              displayEmpty
            >
              <MenuItem value="" disabled>Select Route</MenuItem>
              <MenuItem value="pune-to-mumbai">
                Pune → Mumbai
              </MenuItem>
              <MenuItem value="mumbai-to-pune">
                Mumbai → Pune
              </MenuItem>
            </Select>
            {errors.perPaxRoute && <FormHelperText>{errors.perPaxRoute}</FormHelperText>}
          </FormControl>
        </FormField>
      )}

      {!isOtherService && <FormField>
        <FormControl fullWidth error={!!errors.fleet} disabled={!selectedServiceId}>
          {/* <InputLabel id="fleet-label">Select Vehicle</InputLabel> */}
          <Select
            labelId="fleet-label"
            value={selectedFleetId}
            onChange={handleFleetChange}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select Vehicle
            </MenuItem>
            {fleetOptions.map((fleet) => (  
              <MenuItem key={fleet.id} value={fleet.id.toString()}>
                {fleet.displayName}
              </MenuItem>
            ))}
          </Select>
          {errors.fleet && <FormHelperText>{errors.fleet}</FormHelperText>}
          {!selectedServiceId && (
            <FormHelperText>Please select a service first</FormHelperText>
          )}
        </FormControl>
      </FormField>}
    </>
  );
};

export default ServiceSelectionForm;
